import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { gamificationStore } from '../stores/gamificationStore'
import { mojilandStore } from '../stores/mojilandStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { resources } from '../utils/sets'
import { playSound, vibrate } from '../utils/sounds'
import { 
  AnimalData, 
  CategoryType, 
  categories, 
  getRandomAnimals, 
  getAnimalValue, 
  formatValue 
} from '../utils/animals'

type GamePhase = 'menu' | 'categorySelect' | 'playing' | 'gameOver'

interface TimelineCard extends AnimalData {
  id: string
  revealed: boolean
}

const INITIAL_CARDS = 3
const MAX_LIVES = 3
const CARDS_TO_WIN = 15

const TimelineMoji = observer(() => {
  const [phase, setPhase] = useState<GamePhase>('menu')
  const [category, setCategory] = useState<CategoryType>('size')
  const [timeline, setTimeline] = useState<TimelineCard[]>([])
  const [currentCard, setCurrentCard] = useState<TimelineCard | null>(null)
  const [score, setScore] = useState(0)
  const [lives, setLives] = useState(MAX_LIVES)
  const [cardsPlaced, setCardsPlaced] = useState(0)
  const [streak, setStreak] = useState(0)
  const [maxStreak, setMaxStreak] = useState(0)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [lastWasCorrect, setLastWasCorrect] = useState<boolean | null>(null)
  const [resourcesEarned, setResourcesEarned] = useState<Record<string, number>>({})
  const [showingAnswer, setShowingAnswer] = useState(false)
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium')

  const sound = (type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }

  // Iniciar juego
  const startGame = useCallback((selectedCategory: CategoryType, diff: 'easy' | 'medium' | 'hard') => {
    sound('go')
    setCategory(selectedCategory)
    setDifficulty(diff)
    
    // Generar línea temporal inicial
    const initialAnimals = getRandomAnimals(INITIAL_CARDS)
    const sortedAnimals = initialAnimals.sort((a, b) => 
      getAnimalValue(a, selectedCategory) - getAnimalValue(b, selectedCategory)
    )
    
    const initialTimeline: TimelineCard[] = sortedAnimals.map((animal, i) => ({
      ...animal,
      id: `card-${i}`,
      revealed: true
    }))
    
    setTimeline(initialTimeline)
    
    // Generar primera carta para colocar
    const usedEmojis = initialAnimals.map(a => a.emoji)
    const [newAnimal] = getRandomAnimals(1, usedEmojis)
    setCurrentCard({
      ...newAnimal,
      id: 'current-0',
      revealed: false
    })
    
    setPhase('playing')
    setScore(0)
    setLives(MAX_LIVES)
    setCardsPlaced(0)
    setStreak(0)
    setMaxStreak(0)
    setLastWasCorrect(null)
    setResourcesEarned({})
  }, [soundEnabled])

  // Colocar carta en la línea temporal
  const placeCard = useCallback((selectedIndex: number) => {
    if (!currentCard || showingAnswer) return
    
    const currentValue = getAnimalValue(currentCard, category)
    
    // Encontrar la posición CORRECTA donde debería ir
    let correctIndex = 0
    for (let i = 0; i < timeline.length; i++) {
      const cardValue = getAnimalValue(timeline[i], category)
      if (currentValue > cardValue) {
        correctIndex = i + 1
      } else {
        break
      }
    }
    
    // Insertar SIEMPRE en la posición correcta
    const newTimeline = [...timeline]
    newTimeline.splice(correctIndex, 0, { ...currentCard, revealed: true })
    
    // Verificar si el usuario eligió la posición correcta
    const isCorrect = selectedIndex === correctIndex
    
    setTimeline(newTimeline)
    setCurrentCard(null)
    setShowingAnswer(true)
    setLastWasCorrect(isCorrect)
    
    if (isCorrect) {
      // Carta correcta!
      const newStreak = streak + 1
      const multiplier = difficulty === 'hard' ? 3 : difficulty === 'medium' ? 2 : 1
      const streakBonus = newStreak > 5 ? 3 : newStreak > 3 ? 2 : 1
      const points = 10 * multiplier * streakBonus
      
      setScore(s => s + points)
      setStreak(newStreak)
      if (newStreak > maxStreak) setMaxStreak(newStreak)
      setCardsPlaced(c => c + 1)
      
      if (newStreak > 5) {
        sound('superCombo')
        vibrate([30, 20, 30, 20, 30])
      } else if (newStreak > 3) {
        sound('combo')
        vibrate([30, 20, 30])
      } else {
        sound('match')
        vibrate(50)
      }
      
      setTimeout(() => {
        setShowingAnswer(false)
        
        // Verificar si ganó
        if (cardsPlaced + 1 >= CARDS_TO_WIN) {
          sound('win')
          endGame(true)
        } else {
          // Generar siguiente carta
          const usedEmojis = newTimeline.map(a => a.emoji)
          const [newAnimal] = getRandomAnimals(1, usedEmojis)
          setCurrentCard({
            ...newAnimal,
            id: `current-${cardsPlaced + 1}`,
            revealed: false
          })
        }
      }, 2000)
      
    } else {
      // Carta incorrecta - pero se muestra en la posición correcta en rojo
      sound('wrong')
      vibrate(200)
      setStreak(0)
      const newLives = lives - 1
      setLives(newLives)
      
      setTimeout(() => {
        setShowingAnswer(false)
        
        if (newLives <= 0) {
          sound('lose')
          endGame(false)
        } else {
          // Generar siguiente carta
          const usedEmojis = newTimeline.map(a => a.emoji)
          const [newAnimal] = getRandomAnimals(1, usedEmojis)
          setCurrentCard({
            ...newAnimal,
            id: `current-${cardsPlaced}`,
            revealed: false
          })
        }
      }, 2000)
    }
  }, [currentCard, category, timeline, lives, streak, maxStreak, cardsPlaced, soundEnabled, showingAnswer, difficulty])
  
  // Soporte para teclado numérico
  useEffect(() => {
    if (phase !== 'playing' || !currentCard || showingAnswer) return
    
    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key
      const num = parseInt(key)
      
      // Si es un número válido (0-9)
      if (!isNaN(num) && num >= 0 && num <= timeline.length) {
        placeCard(num)
      }
    }
    
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [phase, currentCard, showingAnswer, timeline.length, placeCard])

  // Terminar juego
  const endGame = (won: boolean) => {
    const finalScore = score + (won ? 500 : 0)
    setScore(finalScore)
    
    const earned: Record<string, number> = {}
    const resourceReward = Math.floor(finalScore / 30) + Math.floor(maxStreak / 2)
    resources.forEach(r => {
      earned[r] = Math.floor(resourceReward / resources.length) + (Math.random() > 0.5 ? 1 : 0)
    })
    setResourcesEarned(earned)
    
    Object.entries(earned).forEach(([icon, amount]) => {
      profileStore.addResource(icon, amount)
    })
    
    leaderboardStore.submitGameScore('timeline', finalScore, 0)
    gamificationStore.recordGameResult('timeline', finalScore, 0, won)
    mojilandStore.tryCompleteFromGame('timeline', finalScore, 0, won)

    setTimeout(() => {
      setPhase('gameOver')
    }, 800)
  }

  const selectedCategory = categories.find(c => c.id === category)!

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-amber-500/90 to-orange-500/90 backdrop-blur-md border-b border-white/20 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to="/"
                className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <span className="text-white text-lg">←</span>
              </Link>
              <span className="text-2xl">🦁</span>
              <div>
                <h1 className="font-title text-lg text-white drop-shadow">Timeline 🦁</h1>
                {phase === 'playing' && (
                  <p className="text-white/80 text-xs">{selectedCategory.emoji} {selectedCategory.name}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {phase === 'playing' && (
                <>
                  <div className="hidden sm:flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
                    <span className="text-white font-bold text-sm">🏆 {score}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
                    {Array.from({ length: MAX_LIVES }).map((_, i) => (
                      <span key={i} className="text-lg">
                        {i < lives ? '❤️' : '🖤'}
                      </span>
                    ))}
                  </div>
                </>
              )}
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <span className="text-lg">{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>
          
          {phase === 'playing' && (
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                  <span className="text-white/80 text-xs">Cartas: </span>
                  <span className="text-white font-bold text-sm">{cardsPlaced}/{CARDS_TO_WIN}</span>
                </div>
                {streak > 0 && (
                  <div className={`px-3 py-1 rounded-lg text-sm font-bold animate-pulse ${
                    streak > 5 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                    streak > 3 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                    'bg-white/30'
                  }`}>
                    <span className="text-white">🔥 {streak}</span>
                  </div>
                )}
              </div>
              <div className="sm:hidden bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                <span className="text-white font-bold text-sm">🏆 {score}</span>
              </div>
            </div>
          )}
        </div>
      </header>
      
      <div className="flex-1 relative">
        {/* Menu inicial */}
        {phase === 'menu' && (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="glass-card p-8 max-w-lg w-full">
              <div className="text-center mb-6">
                <span className="text-7xl block mb-4">🦁</span>
                <h2 className="font-title text-3xl text-gray-800 mb-2">Timeline 🦁</h2>
                <p className="text-gray-600">Ordena los animales según sus características</p>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="text-left">
                  <p className="text-sm text-gray-600 mb-2 font-semibold">📚 Cómo jugar:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Verás una línea de animales ordenados</li>
                    <li>• Coloca cada nuevo animal en su posición correcta</li>
                    <li>• ¡Construye rachas para multiplicar puntos!</li>
                    <li>• Completa {CARDS_TO_WIN} cartas para ganar</li>
                  </ul>
                </div>
              </div>
              
              <button
                onClick={() => setPhase('categorySelect')}
                className="w-full px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-bold text-lg hover:scale-105 transition-transform shadow-xl shadow-orange-500/30"
              >
                🎮 Jugar
              </button>
            </div>
          </div>
        )}

        {/* Selección de categoría */}
        {phase === 'categorySelect' && (
          <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
            <div className="glass-card p-6 max-w-2xl w-full my-4">
              <div className="text-center mb-6">
                <h2 className="font-title text-2xl text-gray-800 mb-2">Elige el modo de juego</h2>
                <p className="text-gray-600 text-sm">¿Cómo quieres ordenar los animales?</p>
              </div>
              
              {/* Selección de dificultad */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3 font-semibold text-center">Dificultad:</p>
                <div className="grid grid-cols-3 gap-2">
                  {(['easy', 'medium', 'hard'] as const).map((diff) => (
                    <button
                      key={diff}
                      onClick={() => setDifficulty(diff)}
                      className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                        difficulty === diff
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white scale-105'
                          : 'bg-white/50 text-gray-600 hover:bg-white/80'
                      }`}
                    >
                      {diff === 'easy' ? '😊 Fácil (×1)' : diff === 'medium' ? '🎯 Normal (×2)' : '🔥 Difícil (×3)'}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => startGame(cat.id, difficulty)}
                    className="group relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-white to-gray-50 hover:from-amber-50 hover:to-orange-50 border-2 border-gray-200 hover:border-orange-400 transition-all hover:scale-105 hover:shadow-xl"
                  >
                    <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform">
                      {cat.emoji}
                    </span>
                    <p className="font-bold text-gray-800 text-sm">{cat.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{cat.description}</p>
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setPhase('menu')}
                className="mt-6 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300"
              >
                ← Volver
              </button>
            </div>
          </div>
        )}

        {/* Pantalla de juego */}
        {phase === 'playing' && (
          <div className="absolute inset-0 flex flex-col p-4">
            <div className="w-full flex-1 flex flex-col">
              
              {/* Carta actual para colocar */}
              {currentCard && (
                <div className="mb-4 flex justify-center flex-shrink-0">
                  <div className="glass-card p-4 inline-block">
                    <p className="text-center text-gray-600 text-sm mb-2 font-semibold">
                      🎯 Coloca esta carta en la posición correcta
                    </p>
                    <p className="text-center text-gray-500 text-xs mb-3">
                      👆 Haz clic en las flechas o presiona el número en tu teclado (0-9)
                    </p>
                    <div className="bg-gradient-to-br from-white to-amber-50 rounded-2xl p-4 border-4 border-amber-400 shadow-2xl shadow-amber-500/30 w-48 transform hover:scale-105 transition-transform">
                      <div className="text-center">
                        <span className="text-6xl block mb-2">{currentCard.emoji}</span>
                        <p className="font-bold text-gray-800 text-lg">{currentCard.name}</p>
                        <div className="mt-2 bg-amber-100 rounded-lg px-3 py-1">
                          <span className="text-amber-700 font-bold">❓</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje de feedback flotante */}
              {showingAnswer && lastWasCorrect !== null && (
                <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 animate-bounce-in">
                  <div className={`px-8 py-4 rounded-2xl shadow-2xl border-4 ${
                    lastWasCorrect 
                      ? 'bg-green-500 border-green-600 shadow-green-500/50' 
                      : 'bg-red-500 border-red-600 shadow-red-500/50'
                  }`}>
                    <p className="text-center font-bold text-3xl text-white drop-shadow-lg">
                      {lastWasCorrect ? '✅ ¡CORRECTO!' : '❌ INCORRECTO'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Línea temporal con scroll */}
              <div className="flex-1 flex items-center justify-center overflow-hidden">
                <div className="w-full h-full flex items-center">
                  <div className="w-full relative py-8">
                    {/* Línea conectora */}
                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 -translate-y-1/2 z-0 rounded-full" />
                    
                    {/* Contenedor con scroll horizontal */}
                    <div 
                      className="relative z-10 flex items-center gap-4 px-8 overflow-x-auto pb-6"
                      style={{ 
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#f59e0b #fef3c7'
                      }}
                    >
                      {/* Slot inicial */}
                      {!showingAnswer && currentCard && (
                        <button
                          onClick={() => placeCard(0)}
                          onMouseEnter={() => setHoverIndex(-1)}
                          onMouseLeave={() => setHoverIndex(null)}
                          className={`relative flex-shrink-0 w-20 h-28 rounded-xl border-4 border-dashed transition-all flex flex-col items-center justify-center ${
                            hoverIndex === -1
                              ? 'border-orange-500 bg-orange-200 scale-110 shadow-lg'
                              : 'border-gray-400 bg-gray-100 hover:border-orange-400 hover:bg-orange-50'
                          }`}
                        >
                          <span className="text-3xl mb-1">⬇️</span>
                          <span className="text-2xl font-bold text-gray-700 bg-white rounded-full w-8 h-8 flex items-center justify-center">0</span>
                        </button>
                      )}
                      
                      {/* Cartas en timeline */}
                      {timeline.map((card, index) => {
                        // La carta recién colocada es la que tiene el id del currentCard anterior
                        const isJustPlaced = showingAnswer && card.id.startsWith('current-')
                        
                        return (
                          <div key={card.id} className="flex items-center gap-4 flex-shrink-0">
                            <div className={`relative transition-all duration-300 ${
                              isJustPlaced && !lastWasCorrect
                                ? 'animate-shake'
                                : isJustPlaced && lastWasCorrect
                                ? 'scale-110'
                                : ''
                            }`}>
                              {/* Indicador de feedback encima de la carta */}
                              {isJustPlaced && showingAnswer && (
                                <div className={`absolute -top-20 left-1/2 transform -translate-x-1/2 px-5 py-3 rounded-2xl font-bold text-white shadow-2xl z-50 whitespace-nowrap text-lg ${
                                  lastWasCorrect 
                                    ? 'bg-green-500 border-4 border-green-600 animate-bounce' 
                                    : 'bg-red-500 border-4 border-red-600 animate-shake'
                                }`}>
                                  {lastWasCorrect ? '✅ ¡Correcto! Aquí va' : '❌ Iba aquí, no donde lo pusiste'}
                                </div>
                              )}
                              
                              <div className={`bg-gradient-to-br rounded-2xl p-4 shadow-xl w-44 transition-all duration-500 ${
                                isJustPlaced && showingAnswer
                                  ? lastWasCorrect
                                    ? 'border-8 border-green-500 shadow-green-500/70 bg-gradient-to-br from-green-50 to-green-100 scale-105'
                                    : 'border-8 border-red-500 shadow-red-500/70 bg-gradient-to-br from-red-50 to-red-100'
                                  : 'border-4 border-amber-300 from-white to-gray-50'
                              }`}>
                                <div className="text-center">
                                  <span className="text-6xl block mb-2">{card.emoji}</span>
                                  <p className="font-bold text-gray-800 text-base mb-2">{card.name}</p>
                                  {card.revealed && (
                                    <div className={`rounded-lg px-3 py-1.5 ${
                                      isJustPlaced && showingAnswer
                                        ? lastWasCorrect
                                          ? 'bg-green-200 ring-2 ring-green-400'
                                          : 'bg-red-200 ring-2 ring-red-400'
                                        : 'bg-amber-100'
                                    }`}>
                                      <p className={`font-bold text-sm ${
                                        isJustPlaced && showingAnswer
                                          ? lastWasCorrect
                                            ? 'text-green-800'
                                            : 'text-red-800'
                                          : 'text-amber-700'
                                      }`}>
                                        {formatValue(getAnimalValue(card, category), category)}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Slot entre cartas */}
                            {!showingAnswer && currentCard && (
                              <button
                                onClick={() => placeCard(index + 1)}
                                onMouseEnter={() => setHoverIndex(index)}
                                onMouseLeave={() => setHoverIndex(null)}
                                className={`relative flex-shrink-0 w-20 h-28 rounded-xl border-4 border-dashed transition-all flex flex-col items-center justify-center ${
                                  hoverIndex === index
                                    ? 'border-orange-500 bg-orange-200 scale-110 shadow-lg'
                                    : 'border-gray-400 bg-gray-100 hover:border-orange-400 hover:bg-orange-50'
                                }`}
                              >
                                <span className="text-3xl mb-1">⬇️</span>
                                <span className="text-2xl font-bold text-gray-700 bg-white rounded-full w-8 h-8 flex items-center justify-center">{index + 1}</span>
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  
                  {/* Leyenda */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                    <div className="glass-card px-6 py-3 inline-flex items-center gap-3 shadow-xl">
                      <span className="text-2xl">{selectedCategory.emoji}</span>
                      <div>
                        <p className="font-bold text-gray-800">{selectedCategory.name}</p>
                        <p className="text-xs text-gray-500">Ordena de menor a mayor ➡️</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Game Over */}
        {phase === 'gameOver' && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl animate-bounce-in">
              <span className="text-7xl block mb-4">
                {cardsPlaced >= CARDS_TO_WIN ? '🏆' : '💀'}
              </span>
              <h2 className="font-title text-3xl text-amber-600 mb-2">
                {cardsPlaced >= CARDS_TO_WIN ? '¡Victoria!' : 'Game Over'}
              </h2>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 my-6">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-3">
                  <p className="text-3xl font-bold text-amber-600">{score}</p>
                  <p className="text-xs text-gray-600">Puntos</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-3">
                  <p className="text-3xl font-bold text-orange-600">{cardsPlaced}</p>
                  <p className="text-xs text-gray-600">Cartas</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-3">
                  <p className="text-3xl font-bold text-red-600">{maxStreak}</p>
                  <p className="text-xs text-gray-600">Racha</p>
                </div>
              </div>
              
              {/* Resources */}
              {Object.keys(resourcesEarned).length > 0 && (
                <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl">
                  <p className="text-sm text-gray-600 mb-3 font-semibold">Recursos ganados:</p>
                  <div className="flex justify-center gap-4">
                    {Object.entries(resourcesEarned).map(([icon, amount]) => (
                      <div key={icon} className="flex items-center gap-2">
                        <span className="text-2xl">{icon}</span>
                        <span className="font-bold text-amber-600 text-lg">+{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setPhase('categorySelect')}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold hover:scale-105 transition-transform"
                >
                  🔄 Jugar de nuevo
                </button>
                <Link
                  to="/"
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 flex items-center"
                >
                  🏠
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-10px) rotate(-3deg); }
          20%, 40%, 60%, 80% { transform: translateX(10px) rotate(3deg); }
        }
        .animate-shake {
          animation: shake 0.6s ease-in-out;
        }
        @keyframes bounce-in {
          0% { opacity: 0; transform: scale(0.3) translateY(-50px); }
          50% { transform: scale(1.1) translateY(-10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
        
        /* Scroll horizontal personalizado */
        div::-webkit-scrollbar {
          height: 8px;
        }
        div::-webkit-scrollbar-track {
          background: #fef3c7;
          border-radius: 10px;
        }
        div::-webkit-scrollbar-thumb {
          background: #f59e0b;
          border-radius: 10px;
        }
        div::-webkit-scrollbar-thumb:hover {
          background: #d97706;
        }
      `}</style>
    </div>
  )
})

export default TimelineMoji
